import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Tooltip,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Checkbox, Radio, RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2, FiRotateCcw } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';

export default function ViewFiltersModal({ isOpen, onClose, rowGroups, onApply }) {
  const [filters, setFilters] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', config: {} });
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(null);
  const [userDefaultId, setUserDefaultId] = useState(null);

  const fetchFilters = useCallback(async () => {
    try {
      const params = showDeleted ? 'include_deleted=true' : '';
      const [{ data }, meRes] = await Promise.all([
        api.get(`/view-filters?${params}`),
        api.get('/auth/me').catch(() => ({ data: {} }))
      ]);
      setFilters(data);
      setUserDefaultId(meRes.data.default_view_filter_id || null);
    } catch(e) {}
  }, [showDeleted]);

  useEffect(() => { if (isOpen) { fetchFilters(); setShowForm(false); setEditingId(null); } }, [isOpen, fetchFilters]);

  const allGroups = rowGroups || [];

  const initConfig = () => {
    const c = {};
    allGroups.forEach(g => { c[g.id] = 'show'; });
    return c;
  };

  const openNew = () => {
    setForm({ name: '', config: initConfig() });
    setEditingId(null); setShowForm(true);
  };

  const openEdit = (f) => {
    let config = {};
    try { config = typeof f.filter_config === 'string' ? JSON.parse(f.filter_config) : (f.filter_config || {}); } catch(e) {}
    // Fill in any missing groups
    const full = {};
    allGroups.forEach(g => { full[g.id] = config[g.id] || config.default || 'show'; });
    setForm({ name: f.name, config: full });
    setEditingId(f.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/view-filters/${editingId}`, { name: form.name, filter_config: form.config });
      } else {
        await api.post('/view-filters', { name: form.name, filter_config: form.config });
      }
      await fetchFilters(); setShowForm(false); setEditingId(null);
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => setConfirmDel(id);
  const doDelete = async () => { await api.delete(`/view-filters/${confirmDel}`); await fetchFilters(); setConfirmDel(null); };

  const handleUndelete = async (id) => {
    await api.post(`/view-filters/${id}/undelete`); await fetchFilters();
  };

  const saveAsDefault = async (filterId) => {
    try {
      const newDefault = userDefaultId === filterId ? null : filterId;
      await api.put('/view-filters/user-default', { view_filter_id: newDefault });
      setUserDefaultId(newDefault);
      toast({ title: newDefault ? 'Default filter set' : 'Default filter cleared', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const setGroupVis = (gid, vis) => {
    setForm(p => ({ ...p, config: { ...p.config, [gid]: vis } }));
  };

  const setAll = (vis) => {
    const c = {};
    allGroups.forEach(g => { c[g.id] = vis; });
    setForm(p => ({ ...p, config: c }));
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            View Filters
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={openNew}>{showForm && !editingId ? 'Cancel' : 'New Filter'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <HStack mb={3}>
            <Checkbox size="sm" isChecked={showDeleted} onChange={e => setShowDeleted(e.target.checked)}>
              <Text fontSize="10px">{showDeleted ? 'Hide Deleted' : 'Show Deleted'}</Text>
            </Checkbox>
          </HStack>

          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <FormControl mb={2}><FormLabel fontSize="xs" mb={0.5}>Filter Name *</FormLabel>
                <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
              <HStack mb={2} spacing={1}>
                <Button size="xs" variant="outline" onClick={() => setAll('show')}>All Show</Button>
                <Button size="xs" variant="outline" onClick={() => setAll('hide')}>All Hide</Button>
                <Button size="xs" variant="outline" onClick={() => setAll('hide_if_empty')}>All Hide if Empty</Button>
              </HStack>
              <Box maxH="250px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={2} bg="white">
                {allGroups.map(g => (
                  <Flex key={g.id} align="center" justify="space-between" py={1} borderBottom="1px" borderColor="gray.50">
                    <HStack spacing={2}>
                      <Box w="10px" h="10px" borderRadius="sm" bg={g.color || '#2d3748'} />
                      <Text fontSize="11px" fontWeight="500">{g.name}</Text>
                    </HStack>
                    <RadioGroup size="sm" value={form.config[g.id] || 'show'}
                      onChange={(val) => setGroupVis(g.id, val)}>
                      <Stack direction="row" spacing={2}>
                        <Radio value="show"><Text fontSize="9px">Show</Text></Radio>
                        <Radio value="hide"><Text fontSize="9px">Hide</Text></Radio>
                        <Radio value="hide_if_empty"><Text fontSize="9px">Hide if Empty</Text></Radio>
                      </Stack>
                    </RadioGroup>
                  </Flex>
                ))}
              </Box>
              <Flex justify="flex-end" mt={2}>
                <Button size="xs" variant="ghost" mr={2} onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving}>{editingId ? 'Update' : 'Create Filter'}</Button>
              </Flex>
            </Box>
          )}

          {filters.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No filters.</Text>
          ) : (
            <VStack spacing={1} align="stretch">
              {filters.map(f => (
                <Flex key={f.id} px={2} py={1.5} border="1px"
                  borderColor={f.deleted_at ? 'red.200' : userDefaultId === f.id ? 'orange.300' : 'gray.100'}
                  borderRadius="md" align="center" justify="space-between"
                  opacity={f.deleted_at ? 0.5 : 1}
                  bg={f.deleted_at ? 'red.50' : userDefaultId === f.id ? 'orange.50' : 'white'}>
                  {/* Default tick */}
                  <Tooltip label={userDefaultId === f.id ? 'Default on load (click to unset)' : 'Set as default on load'} fontSize="xs">
                    <Box w="22px" h="22px" flexShrink={0} border="2px solid"
                      borderColor={userDefaultId === f.id ? 'orange.400' : 'gray.300'}
                      bg={userDefaultId === f.id ? 'orange.400' : 'white'}
                      borderRadius="4px" cursor="pointer" mr={2}
                      display="flex" alignItems="center" justifyContent="center"
                      onClick={() => saveAsDefault(f.id)}>
                      {userDefaultId === f.id && <Box as="span" fontSize="11px" color="white" fontWeight="800">✓</Box>}
                    </Box>
                  </Tooltip>
                  <HStack spacing={2} flex={1}>
                    <Text fontSize="12px" fontWeight="600">{f.name}</Text>
                    {!!f.is_system && <Badge fontSize="7px" colorScheme="blue">System</Badge>}
                    {userDefaultId === f.id && <Badge fontSize="7px" colorScheme="orange">Default</Badge>}
                    {!!f.deleted_at && <Badge fontSize="7px" colorScheme="red">DELETED</Badge>}
                  </HStack>
                  <HStack spacing={1}>
                    <Button size="xs" variant="ghost" fontSize="9px" colorScheme="teal"
                      onClick={() => { if (onApply) onApply(f); onClose(); }}>Apply</Button>
                    {!f.deleted_at && !f.is_system && (
                      <>
                        <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(f)} aria-label="Edit" />
                        <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(f.id)} aria-label="Delete" />
                      </>
                    )}
                    {!!f.deleted_at && (
                      <IconButton icon={<FiRotateCcw />} size="xs" variant="ghost" colorScheme="blue" onClick={() => handleUndelete(f.id)} aria-label="Restore" />
                    )}
                  </HStack>
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)}
      title="Delete View Filter" message="Are you sure you want to delete this view filter?"
      confirmLabel="Delete" confirmColor="red" onConfirm={doDelete} />
    </>
  );
}
